/* eslint-disable max-statements */
import { Button, Center, Flex, LoadingOverlay, ScrollArea, Stack, Text, Timeline } from '@mantine/core';
import { IconCircle } from '@tabler/icons-react';
import moment from 'moment';
import prettifyXml from 'prettify-xml';
import { useEffect, useMemo, useState } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { useCustomsNotifications, useCustomsSync } from '@admin/hooks';
import { UpdateCorrelationId } from './UpdateCorrelationId';

SyntaxHighlighter.registerLanguage('xml', xml);

const renderSegmentedControlLabel = (label) => (
  <Flex mih={32} align="center" justify="flex-start">
    {label}
  </Flex>
);
const prettifyXmlOptions = { indent: 6, newline: '\n' };

export const NotificationsList = ({ selectedSystem, selectedDeclarant, selectedLRN, selectedMRN, selectedCorrelationID, selectedTransitGroup }) => {
  const { data: { notifications, correlationId, customsSyncAvailable, entityId } = {}, isLoading } = useCustomsNotifications({
    system: selectedSystem.value,
    declarantId: selectedDeclarant.value,
    lrn: selectedLRN,
    mrn: selectedMRN,
    correlationId: selectedCorrelationID,
    transitGroup: selectedTransitGroup,
  });

  const [selectedNotification, setSelectedNotification] = useState({
    value: '',
    code: '',
    date: '',
    label: <></>,
    notificationXML: '',
    correlationId: '',
    index: 0,
    isInEvents: false,
  });
  const customsSyncMutation = useCustomsSync();

  const handleCustomsSync = () => {
    customsSyncMutation.mutate({
      body: {
        system: selectedSystem.value,
        entityId: entityId,
        declarantId: selectedDeclarant.value,
      },
    });
  };

  const notificationsList = useMemo(() => {
    return (notifications || []).map((n) => ({
      value: n.href,
      code: n.code,
      date: moment(n.date).format('DD-MM-YY @ HH:mm:ss'),
      label: renderSegmentedControlLabel(n.href),
      notificationXML: prettifyXml(n.xml, prettifyXmlOptions),
      correlationId: n.correlationId,
      isInEvents: n.isInEvents,
    }));
  }, [notifications]);

  useEffect(() => {
    if (notificationsList.length > 0) {
      setSelectedNotification({
        ...notificationsList[0],
        index: 0,
      });
    }
  }, [notificationsList]);

  const handleNotificationChange = (value) => {
    const index = notificationsList.findIndex((n) => n.value === value);
    if (index !== -1) {
      setSelectedNotification({
        ...notificationsList[index],
        index,
      });
    }
  };

  const handleCopyXml = () => {
    navigator.clipboard.writeText(selectedNotification?.notificationXML || '');
  };

  const handleCopyCorrelationId = () => {
    const correlationId = selectedNotification?.correlationId || '';
    if (correlationId) {
      navigator.clipboard.writeText(correlationId);
    }
  };

  if (!notifications) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  if (!notifications.length) {
    return <Center>No notifications</Center>;
  }

  return (
    <Flex gap={64} align="flex-start">
      <Timeline active={selectedNotification?.index} bulletSize={24} lineWidth={2} color="blue" miw={200}>
        {notificationsList.map((item) => (
          <Timeline.Item
            key={item.value}
            bullet={<IconCircle size={12} />}
            onClick={() => handleNotificationChange(item.value)}
            title={
              <Stack
                fw={selectedNotification?.value === item.value ? 'bold' : 'normal'}
                c={selectedNotification?.value === item.value ? 'blue' : 'inherit'}
                style={{ cursor: 'pointer' }}
                gap={1}
              >
                <Text>{item.code}</Text>
                <Text>{item.date}</Text>
                <Text c={item.isInEvents ? 'green' : 'red'}>{item.isInEvents ? 'In Events' : 'Not in Events'}</Text>
              </Stack>
            }
          />
        ))}
      </Timeline>

      <Stack style={{ flexGrow: 1 }} gap={10}>
        <Flex gap={20} align="center" mb={10}>
          {correlationId && customsSyncAvailable && (
            <Button type="button" onClick={handleCustomsSync} disabled={!customsSyncAvailable} variant="outline" size="lg">
              Customs Sync
            </Button>
          )}
          <UpdateCorrelationId
            selectedSystem={selectedSystem}
            selectedLRN={selectedLRN}
            selectedMRN={selectedMRN}
            selectedTransitGroup={selectedTransitGroup}
            correlationId={correlationId}
          ></UpdateCorrelationId>
        </Flex>

        <Flex direction="row" style={{ flexGrow: 1 }}>
          <Flex justify="flex-start" pr={10} direction="column">
            <Button type="button" onClick={handleCopyXml} variant="outline" size="xs">
              Copy XML
            </Button>
            <Button type="button" onClick={handleCopyCorrelationId} variant="outline" size="xs" mt={10}>
              Copy Correlation ID
            </Button>
          </Flex>

          <ScrollArea pos="relative">
            <SyntaxHighlighter
              language="xml"
              style={vs}
              customStyle={{
                fontSize: '14px',
                padding: '12px',
                fontFamily: 'monospace',
              }}
            >
              {selectedNotification.notificationXML}
            </SyntaxHighlighter>
          </ScrollArea>
        </Flex>
      </Stack>
    </Flex>
  );
};
